import React, {useEffect, useRef} from 'react';
import * as d3 from 'd3';
import {
  ExtendedIChild,
  ExtendedIConceptExplorerData,
  IChild,
  IConceptExplorerData,
} from './TypesHeatMap';
import {Box} from '@mui/material';
import ArrTooltip from '../../../common/arr-tooltip/ArrTooltip';
import ClusterTooltip from './ClusterTooltip';
import {createRoot} from 'react-dom/client';
import Tiles from './Tiles';

interface IHeatedMap {
  data: IConceptExplorerData;
  selectedCluster: {[key: number]: string | null};
  setSelectedCluster: React.Dispatch<
    React.SetStateAction<{[key: number]: string | null}>
  >;
  searchId: number | string;
  setCartItems: React.Dispatch<React.SetStateAction<IChild[]>>;
  cartItems: IChild[];
  currentLevelIndex: number;
}
const colorsAndScale = {
  default: ' #D3E4FF',
  selected: '#2B3585',
  inCart: ' #424750',
  scale: {max: '#9F75DF', min: '#E8DEF7'},
};
const HeatedMap = ({
  data,
  selectedCluster,
  setSelectedCluster,
  setCartItems,
  cartItems,
  currentLevelIndex,
}: IHeatedMap) => {
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: IChild | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });

  const [size, setSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  const svgRef = useRef(null);
  const clusterLabelsRef = useRef(new Map());

  useEffect(() => {
    const handleResize = () => {
      const doc = document.getElementsByClassName(
        'clusters-container'
      )[0] as HTMLElement;
      setSize({
        width: doc.getBoundingClientRect().width - 64,
        height: doc.getBoundingClientRect().height - 64,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!data || !size) return;
    const svg = d3
      .select(svgRef.current)
      .attr('width', size.width)
      .attr('height', size.height);

    const clonedData: ExtendedIConceptExplorerData = JSON.parse(
      JSON.stringify(data)
    );
    const maxSize = clonedData.children.reduce(
      (max, child) => Math.max(max, child.size),
      0
    );
    clonedData.children.forEach((child: ExtendedIChild) => {
      child.value = child.size / maxSize > 0.5 ? child.size : maxSize * 0.5;
    });

    const minValue = Math.min(
      ...clonedData.children.map((d) => Number(d.hdocs))
    );
    const maxValue = Math.max(
      ...clonedData.children.map((d) => Number(d.hdocs))
    );

    const colorScale = d3
      .scaleLinear<string, string>()
      .domain([minValue, maxValue])
      .range([colorsAndScale.scale.min, colorsAndScale.scale.max])
      .clamp(true);

    const root = d3
      .hierarchy<ExtendedIConceptExplorerData>(clonedData)
      .sum((d) => d.value)
      .sort((a: any, b: any) => a.value - b.value);

    //treemap layout
    const treemap = d3
      .treemap<ExtendedIConceptExplorerData>()
      .size([size.width, size.height])
      .padding(1)
      .round(true);

    treemap(root);

    const tiles = svg
      .selectAll('g')
      .data(root.leaves())
      .join('g')
      .attr('transform', (d: any) => `translate(${d.x0},${d.y0})`);

    //rect for each tile
    tiles
      .selectAll('rect')
      .data((d: any) => [d])
      .join('rect')
      .attr('key', (d: any) => `${d.data.id}-rect`)
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('class', 'heatmap')
      .attr('fill', (d: any) =>
        selectedCluster[currentLevelIndex] === d.data.id
          ? colorsAndScale.selected
          : cartItems.find((item) => item.id === d.data.id)
            ? colorsAndScale.inCart
            : d.data.hdocs === '0'
              ? colorsAndScale.default
              : colorScale(d.data.hdocs)
      );

    //text label for boxes
    const foreignObjects = tiles
      .selectAll('foreignObject')
      .data((d: any) => [d])
      .join('foreignObject')
      .attr('key', (d: any) => d.data.id)
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .on('mouseenter', (event: React.MouseEvent<SVGRectElement>, d: any) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltip({
          visible: true,
          x: rect.left + rect.width / 2,
          y: rect.top,
          content: d.data,
        });
      })
      .on('mouseleave', () => {
        setTooltip({
          visible: false,
          x: 0,
          y: 0,
          content: null,
        });
      })
      .on('click', (_, d: any) => {
        setSelectedCluster((prev) => ({
          ...prev,
          [currentLevelIndex]:
            prev[currentLevelIndex] === d.data.id ? null : d.data.id,
        }));
      });
    foreignObjects.each(function (d: any, index: number) {
      const textBoxClass =
        (this as HTMLElement).getElementsByClassName('text-box') || [];
      textBoxClass[0]?.remove();
      const container = document.createElement('div');
      container.className = 'text-box';
      (this as HTMLElement).appendChild(container);
      const root = createRoot(container);
      root.render(
        <Tiles
          item={d.data}
          isSelected={selectedCluster[currentLevelIndex] === d.data.id}
          setSelectedCluster={setSelectedCluster}
          currentLevelIndex={currentLevelIndex}
          cartItems={cartItems}
          setCartItems={setCartItems}
          dataTestId={`tile-${index + 1}`}
        />
      );
      clusterLabelsRef.current.set(d.data.id, root);
    });
  }, [data, size?.height, size?.width, selectedCluster, cartItems]);
  return (
    <Box
      className="clusters-container"
      sx={{position: 'relative'}}
      data-testid="lbl-cluster-title"
    >
      <svg
        ref={svgRef}
        width={size?.width}
        height={size?.height}
        tabIndex={0}
        aria-label="heated-map-container"
        data-testid="arr-cluster-heatmap-color"
      />
      {tooltip.visible && tooltip.content && (
        <div
          className="svg-div"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
          data-testid={`heated-map-div`}>
          <ArrTooltip
            defaultOpen={tooltip.visible}
            content={<ClusterTooltip clusterData={tooltip.content} />}
            //@ts-expect-error-- type mismatch
            PopperProps={{
              anchorEl: {
                getBoundingClientRect: () => ({
                  top: tooltip.y,
                  left: tooltip.x,
                  right: tooltip.x,
                  bottom: tooltip.y,
                  width: 0,
                  height: 0,
                }),
              },
            }}
            flipEnabled={false}
          />
        </div>
      )}
    </Box>
  );
};

export default React.memo(HeatedMap);
